/**
 * Help tab — self-service before a ticket. Search + topics contextual to the
 * page the user opened the launcher from, rendered from the (previously
 * unmounted) help content library.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, BookOpen, Search } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import type { HelpTopic } from '../../../data/helpContent';
import { contextualHelp, popularHelp, searchHelp } from '../../../services/supportHub/helpSearch';

export const HelpTab: React.FC = () => {
  const { userProfile } = useAuth();
  const location = useLocation();
  const [search, setSearch] = React.useState('');
  const [topic, setTopic] = React.useState<HelpTopic | null>(null);

  const role = userProfile?.role;
  const results = search.trim()
    ? searchHelp(search, role)
    : contextualHelp(location.pathname, role);
  const showing = results.length > 0 ? results : popularHelp(role);
  const heading = search.trim()
    ? 'Search results'
    : results.length > 0
      ? 'Help for this page'
      : 'Popular topics';

  if (topic) {
    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setTopic(null)}
          className="flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> All topics
        </button>
        <h3 className="text-lg font-semibold text-neutral-900">{topic.title}</h3>
        <div className="prose prose-sm max-w-none text-neutral-800">
          <ReactMarkdown>{topic.content}</ReactMarkdown>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search help — e.g. capture a deal, assign a lead…"
          className="input-field pl-9"
          autoFocus
        />
      </div>

      <p className="text-xs font-medium uppercase tracking-wide text-neutral-400">{heading}</p>
      <div className="space-y-2">
        {showing.length === 0 && (
          <p className="py-6 text-center text-sm text-neutral-400">
            Nothing found — try different words, or ask us directly in the Question tab.
          </p>
        )}
        {showing.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTopic(t)}
            className="flex w-full items-start gap-3 rounded-lg border border-neutral-100 bg-white p-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
          >
            <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>
              <span className="block text-sm font-medium text-neutral-900">{t.title}</span>
              <span className="block text-xs text-neutral-500">{t.description}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default HelpTab;
